<template>
	<div
		:class="[
			allErrors.length > 0 ? 'print:hidden' : '',
			'grid grid-cols-1 grid-rows-1 gap-14 lg:grid-cols-2',
		]"
	>
		<BlobImage
			class="h-auto w-full rounded-xl object-cover print:hidden"
			:src="getPlanImage(plan.id)"
			:alt="plan.lastName"
		/>

		<div class="space-y-5">
			<header class="mb-5 flex items-center justify-between">
				<div class="flex items-start gap-2">
					<PlanAvatar
						:plan="plan"
						:index="index"
						filled
						class="cursor-default!"
					/>
					<div class="text-2xl">{{ plan.firstName }} {{ plan.lastName }}</div>
				</div>

				<Cog8ToothIcon
					class="text-secondary-300 h-6 w-6 cursor-pointer print:hidden"
					@click="openPaymentModal(plan)"
					v-if="!planEncrypted"
				/>
			</header>

			<ConfirmIdentity
				:plan="plan"
				v-if="planEncrypted"
			/>

			<div
				v-if="allErrors.length > 0 && !planEncrypted"
				class="space-y-2"
			>
				<BaseAlert
					v-for="error in allErrors"
					:key="error"
					type="warning"
				>
					<template v-if="error === cartItemsError">
						{{ t('paymentOptionsSlide.errors.noCartItemsInPlanStart') }}
						<button
							type="button"
							@click="goToPackagesSlide"
							class="text-primary-500 underline"
						>
							{{
								t('paymentOptionsSlide.errors.noCartItemsInPlanHere')
							}}</button
						>.
					</template>

					<template v-else>
						{{ error }}
					</template>
				</BaseAlert>
			</div>

			<!-- Payment Terms without prices for when showTerms is false -->
			<div
				v-if="!showTerms"
				class="grid grid-cols-2 gap-2 2xl:grid-cols-3"
			>
				<div
					v-for="option in paymentTermOptions.termOptions"
					:key="option.term"
					class="flex flex-col items-center justify-center rounded-lg border-2 border-slate-100 bg-white px-4 py-8 select-none"
				>
					<span class="text-2xl font-semibold text-slate-500">
						{{ option.label }}
					</span>
				</div>
			</div>

			<div v-else>
				<RadioGroup
					v-model="plan.insurance.term"
					class="grid grid-cols-2 gap-2 2xl:grid-cols-3"
				>
					<RadioGroupOption
						v-slot="{ checked }"
						v-for="option in paymentTermOptions.termOptions"
						:key="option.term"
						:value="option.term"
					>
						<div
							class="flex cursor-pointer flex-col items-start justify-center rounded-lg border-2 p-4"
							:class="checked ? 'border-accent-500' : 'border-slate-100'"
						>
							<div class="mb-1 flex w-full items-center justify-between">
								<span class="text-base font-semibold text-slate-500">
									{{ option.label }}
								</span>

								<!-- POPULAR tag for Ten Year term -->
								<span
									v-if="option.term === Term.TenYear"
									class="bg-accent-400 text-accent-700 flex items-center justify-center rounded-full px-4 pt-1.5 pb-1 text-[10px] leading-none font-semibold uppercase"
								>
									{{ t('general.popular') }}
								</span>
							</div>
							<div
								class="text-secondary-900 text-2xl font-semibold md:text-3xl"
							>
								{{ option.formattedPayment }}
							</div>
						</div>
					</RadioGroupOption>
				</RadioGroup>
			</div>

			<div
				class="flex justify-between"
				v-if="!planEncrypted"
			>
				<!-- Health Question Toggles-->
				<div
					class="flex justify-start gap-4"
					:class="{
						'border-b border-slate-200 pb-4 print:border-0 print:pb-0':
							scenarioOutput && scenarioOutput.isValid,
						'print:hidden':
							!plan.insurance.terminallyIll && !plan.insurance.otherIllness,
					}"
				>
					<BaseToggle
						v-model="plan.insurance.terminallyIll"
						:label="t('payment.term.terminalIllness')"
						leftToggle
						capitalize
					/>

					<BaseToggle
						v-model="plan.insurance.otherIllness"
						:label="t('payment.term.otherIllnessHealthQuestions')"
						leftToggle
						capitalize
					/>
				</div>

				<InformationCircleIcon
					class="text-secondary-400 h-5 w-5 cursor-pointer hover:text-slate-500 print:hidden"
					@click="healthQuestionsModalOpen = true"
				/>
			</div>

			<!-- Total Cost -->
			<div
				class="text-md mt-5 flex w-full flex-wrap items-start justify-between gap-6 print:mt-2"
				v-if="scenarioOutput && scenarioOutput.isValid"
			>
				<div class="text-primary-700 font-bold">
					{{ t('payment.totalFuneralAmount') }}
				</div>
				<div class="font-medium text-slate-500">
					{{ formatPrice(totalFuneralAmount) }}
				</div>
			</div>

			<!-- Due Today Dropdown-->
			<DropdownDueToday
				v-if="totalDueTodayAmount"
				:oap-payment="oapPrice"
				:was-oap-initially-included="wasOapInitiallyIncluded"
				:first-payment="firstPaymentAmount ?? 0"
				:total-due-today="totalDueTodayAmount"
				:plan="plan"
				:everplans-price="everplansPrice"
				:was-everplans-initially-included="wasEverplansInitiallyIncluded"
			/>

			<!-- Payment Summary Label Options -->
			<template v-if="totalDueTodayAmount">
				<div
					v-for="(option, index) in paymentSummaryOptions"
					:key="index"
					class="text-secondary-900 mt-5 flex w-full flex-wrap items-end justify-between gap-6 justify-self-end pl-12 text-xl font-semibold xl:pl-48 print:gap-2"
				>
					<div>
						<span>{{ option.label }}</span>
					</div>
					<span>{{ formatPrice(option.amount) }}</span>
				</div>
			</template>

			<div class="flex w-full justify-end">
				<button
					type="button"
					@click="goToDocuments"
					:disabled="!plan.insurance.term"
					class="bg-accent-400 text-accent-700 hover:bg-accent-700 disabled:bg-secondary-300 disabled:hover:bg-secondary-300 mt-4 w-63.75 rounded-lg p-3 text-center font-bold hover:text-white disabled:cursor-not-allowed disabled:text-white print:hidden"
				>
					{{ t('general.complete') }}
				</button>
			</div>
		</div>

		<!-- Payment Options Modal -->
		<precoa-modal v-model:open="paymentModalOpen">
			<template #default>
				<div class="flex flex-col pt-2">
					<div class="text-md text-secondary-900 my-4 text-center font-bold">
						{{ t('payment.adjustPlan') }}
					</div>
					<FormKit
						type="form"
						:actions="false"
					>
						<div
							class="section-grid-row grid grid-cols-2 pb-2 *:w-full *:max-w-none *:px-1 *:pb-1"
						>
							<FormKit
								type="group"
								name="insurance"
							>
								<FormKit
									type="select"
									:label="t('payment.paymentType')"
									name="paymentType"
									:options="paymentTypeOptions"
									:placeholder="
										t('plan.placeholders.select', {
											placeholderLabel: t('payment.paymentType'),
										})
									"
									outer-class="max-w-full w-full"
									v-model="plan.insurance.paymentType"
								/>
								<FormKit
									type="select"
									:label="t('payment.paymentFrequency')"
									name="paymentFrequency"
									:options="paymentFrequencyOptions"
									:placeholder="
										t('plan.placeholders.select', {
											placeholderLabel: t('payment.paymentFrequency'),
										})
									"
									:disabled="isPaymentFrequencyDisabled"
									outer-class="max-w-full w-full"
									v-model="plan.insurance.paymentFrequency"
								/>
								<FormKit
									type="number"
									name="downPayment"
									:label="t('payment.downPayment')"
									:step="100"
									number
									:validation="[['downPaymentMinRule']]"
									:validation-rules="{
										downPaymentMinRule: (node: FormKitNode) => {
											const value = Number(node.value)
											return !value || value >= 500
										},
									}"
									:validation-messages="{
										downPaymentMinRule:
											'Down payment must be at least $500 if entered',
									}"
									validation-visibility="live"
									:disabled="isDownPaymentDisabled"
									@blur="handleDownPaymentBlur"
									v-model="plan.insurance.downPayment"
								/>
							</FormKit>

							<div>
								<FormKit
									type="date"
									name="birthDate"
									:label="t('plan.birthDate')"
									:validation="[
										['required', true],
										['date_between', pastDateMin, currentDate],
									]"
									:outer-class="{ 'mb-2!': true }"
									validation-visibility="live"
									v-model="localBirthDate"
								/>
								<div
									v-if="age"
									class="text-sm font-medium text-slate-500"
								>
									{{ t('payment.ageIs') }} {{ age }}.
								</div>
							</div>
						</div>
						<FormKit
							type="group"
							name="payment"
						>
							<div
								v-if="plan.oapIncluded"
								class="section-grid-row grid grid-cols-1 *:w-full *:max-w-none *:px-1 *:pb-1"
							>
								<FormKit
									type="select"
									:label="t('payment.oapPaymentFrequency')"
									name="oapPaymentFrequency"
									:options="oapPaymentFrequencyOptions"
									:placeholder="
										t('plan.placeholders.select', {
											placeholderLabel: t('payment.paymentFrequency'),
										})
									"
									outer-class="max-w-full w-full"
									v-model="oapPaymentCount"
								/>
							</div>
						</FormKit>
						<Transition
							enter-active-class="transition-all duration-400 ease-in"
							leave-active-class="transition-all duration-200 ease-out"
							enter-from-class="opacity-0 max-h-0 translate-y-5"
							enter-to-class="opacity-100 max-h-[200px] translate-y-0"
							leave-from-class="opacity-100 max-h-[200px] translate-y-0"
							leave-to-class="opacity-0 max-h-0 translate-y-5"
						>
							<div
								v-if="!singlePay"
								v-show="showAdjustInputs"
								class="section-grid-row grid grid-cols-2 overflow-hidden *:w-full *:max-w-none *:px-1 *:pb-1"
							>
								<div>
									<FormKit
										type="number"
										:label="t('payment.paymentTitle', { label: 'Target' })"
										:step="10"
										number
										v-model="targetPayment"
									/>

									<div
										v-if="targetTotal !== null"
										class="text-sm font-medium text-slate-500"
									>
										{{ t('payment.targetAmt') }}:
										{{ formatPrice(targetTotal) }}
									</div>
								</div>
								<div>
									<FormKit
										type="number"
										:label="t('payment.paymentTitle', { label: 'Current' })"
										number
										:step="10"
										:modelValue="monthlyPayment"
										:disabled="true"
									/>
									<div class="text-sm font-medium text-slate-500">
										{{ t('payment.currentAmt') }}:
										{{ formatPrice(currentTotalFaceAmt ?? 0) }}
									</div>
								</div>
							</div>
						</Transition>

						<div
							v-if="
								!singlePay &&
								props.plan?.insurance?.paymentType !== PaymentType.SP_EFT
							"
							class="group hover:text-accent-500 flex cursor-pointer items-center justify-end space-x-2 pt-2 pb-4 text-slate-500"
							@click="toggleAdjustPayment"
						>
							<span
								class="group-hover:bg-accent-400 bg-secondary-300 flex h-4 w-4 items-center justify-center rounded-full"
							>
								<PencilIcon class="h-2 w-2 text-xs text-white" />
							</span>
							<span>{{ t('payment.paymentTitle', { label: 'Adjust' }) }}</span>
						</div>

						<div class="text-md font-medium text-slate-500 italic">
							{{ t('payment.totalPaidOverTime') }}:
							{{ formatPrice(totalPaidOverTime) }}
						</div>

						<button
							type="button"
							@click="paymentModalOpen = false"
							class="bg-accent-400 text-accent-700 hover:bg-accent-700 disabled:bg-secondary-300 mt-4 w-full rounded-lg p-3 text-center font-bold hover:text-white print:hidden"
						>
							{{ t('general.done') }}
						</button>
					</FormKit>
				</div>
			</template>
		</precoa-modal>

		<!-- Health Questions Modal -->
		<HealthQuestionsModal v-model="healthQuestionsModalOpen" />
	</div>
</template>

<script setup lang="ts">
import BaseAlert from '../common/BaseAlert.vue'
import BaseToggle from '../common/BaseToggle.vue'
import BlobImage from '../common/BlobImage.vue'
import ConfirmIdentity from '../encryption/ConfirmIdentityInline.vue'
import PlanAvatar from '../presentation/PlanAvatar.vue'
import DropdownDueToday from './DropdownDueToday.vue'
import type Plan from '@/classes/Plan'
import PrecoaModal from '@/components/common/PrecoaModal.vue'
import HealthQuestionsModal from '@/components/merchandise/HealthQuestionsModal.vue'
import { usePresentationLocale } from '@/i18n'
import { useMerchandise } from '@/mixins/merchandise'
import { useOapPlanProvider } from '@/mixins/oapPlanProvider'
import { usePaymentOptions } from '@/mixins/paymentOptions'
import { usePlanStore } from '@/stores/plan'
import { usePresentationController } from '@/stores/presentationController'
import { useSessionStore } from '@/stores/session'
import type { FormKitNode } from '@formkit/core'
import { FormKit } from '@formkit/vue'
import { RadioGroup, RadioGroupOption } from '@headlessui/vue'
import {
	Cog8ToothIcon,
	InformationCircleIcon,
	PencilIcon,
} from '@heroicons/vue/24/solid'
import { PaymentType, ScenarioErrorBag, Term } from '@precoa/calculator-sdk'
import { DateTime } from 'luxon'
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const { t } = usePresentationLocale()

const route = useRoute()
const router = useRouter()

const pastDateMin = '1900-01-01' // Minimum date value
const currentDate = new Date().toISOString().split('T')[0] // Current date in YYYY-MM-DD format

const { getPlanImage, formatPrice } = useMerchandise()

const { getPlanPaymentOptions } = usePaymentOptions()

const sessionStore = useSessionStore()

const planStore = usePlanStore()

const { getPlanById, updateOapPaymentCount } = useOapPlanProvider(
	sessionStore.activeOapProvider ?? null
)

const props = withDefaults(
	defineProps<{
		index: number
		plan: Plan
		showTerms: boolean
	}>(),
	{
		showTerms: false,
	}
)

const {
	singlePay,
	scenario,
	paymentTermOptions,
	paymentTypeOptions,
	paymentFrequencyOptions,
	oapPaymentFrequencyOptions,
	paymentSummaryOptions,
	firstPaymentAmount,
	targetPayment,
	targetTotal,
	totalDueTodayAmount,
	totalFuneralAmount,
	totalPaidOverTime,
	monthlyPayment,
	oapPrice,
	scenarioOutput,
	errors: mainErrors,
	handleDownPaymentBlur,
	isDownPaymentDisabled,
	isPaymentFrequencyDisabled,
	everplansPrice,
	wasEverplansInitiallyIncluded,
	wasOapInitiallyIncluded,
} = getPlanPaymentOptions(props.plan)

const planId = props.plan.id ?? ''

const oapPaymentCount = ref<number>(
	getPlanById(props.plan.oapPayment.paymentPlanId)?.paymentCount ?? 1
)

// Use local state for birthDate so that it can be validated before setting plan.birthDate
const localBirthDate = ref<string | undefined>(props.plan.birthDate ?? '')

// Show the adjust payment inputs
const showAdjustInputs = ref(false)

// Open the modal
const paymentModalOpen = ref<boolean>(false)

// Open the health questions modal
const healthQuestionsModalOpen = ref(false)

const planEncrypted = computed(
	() => props.plan.encryptionStatus !== 'unencrypted'
)

/**
 * Navigate to the documents page and include the plan id for the given plan as a query param
 * @param {string} planId the id of the plan we are expecting to fill out documents for
 */
function goToDocuments() {
	router.push({
		path: `/session/${route.params.sessionId}/documents`,
		query: { planId },
	})
}

// The current total face amount
const currentTotalFaceAmt = computed(() => {
	if (!scenarioOutput.value || scenarioOutput.value instanceof ScenarioErrorBag)
		return 0
	return scenarioOutput.value.totalFaceAmt
})

// Toggle the adjust payment inputs
function toggleAdjustPayment() {
	showAdjustInputs.value = !showAdjustInputs.value
}

// The user's age based on their birth date
const age = computed(() => {
	if (!props.plan.birthDate) return
	const birthDate = DateTime.fromISO(props.plan.birthDate)
	const today = DateTime.now()
	return Math.floor(today.diff(birthDate, 'years').years)
})

// Open the payment modal
const openPaymentModal = (plan: Plan) => {
	paymentModalOpen.value = true
}

const allErrors = computed(() => [
	...paymentTermOptions.value.errors,
	...mainErrors.value,
])

/** Go to the packages slide */
function goToPackagesSlide() {
	const controller = usePresentationController()
	const packagesSlide = controller.allSlides?.find(
		(slide) => slide.component === 'packages-slide'
	)
	if (!packagesSlide) {
		throw new Error('Could not find packages slide')
	}
	controller.gotoSlideId(packagesSlide?._uid)
}

/** Error message for when there are no cart items in the plan */
const cartItemsError = t('paymentOptionsSlide.errors.noCartItemsInPlan')

/** The age of the person we are planning for */
function getAge(birthDate: string) {
	return Math.floor(
		DateTime.now().diff(DateTime.fromISO(birthDate), 'years').years
	)
}

/* Watch localBirthDate and update plan.birthDate only if valid
 * This ensures that the user can't update the plan with an invalid birth date accidentally
 */
watch(localBirthDate, (newVal) => {
	const date = DateTime.fromISO(newVal ?? '')
	if (
		date.isValid &&
		date >= DateTime.fromISO(pastDateMin) &&
		date <= DateTime.now()
	) {
		props.plan.birthDate = newVal
	}
})

// Clear localBirthDate and downPayment when modal is closed
watch(paymentModalOpen, (isOpen) => {
	if (!isOpen) {
		localBirthDate.value = props.plan.birthDate ?? ''
		const value = Number(props.plan.insurance.downPayment)
		if (value > 0 && value < 500) {
			props.plan.insurance.downPayment = undefined
		}
	}
})

watch(oapPaymentCount, (newCount) => {
	const oapPayment = updateOapPaymentCount(props.plan.oapPayment, newCount)

	if (!oapPayment) {
		return
	}

	planStore.updatePlan(props.plan.id, {
		oapPayment,
	})
})

/** Expose scenario and errors for unit tests */
defineExpose({
	getAge,
	paymentSummaryOptions,
	oapPaymentFrequencyOptions,
	oapPrice,
	scenario,
	errors: mainErrors,
})
</script>
